'use client';

import tracking, { EventCategory } from '@/helper/tracking';
import { getLastPathnameSegment, linkToString } from '@/helper/urls';
import classNames from 'classnames';
import { usePathname } from 'next/navigation';

import classes from './InfoIcon.module.scss';

export interface InfoIcon {
  size?: number;
  href?: string;
  onClick?: () => void;
  className?: string;
  theme?: 'whiteMagenta' | 'green' | 'blue' | 'whiteGreen' | 'orange';
  trackingLabel?: string;
}

const htmlReplaceRegex = /(<([^>]+)>)/gi;

const InfoIcon: React.FC<InfoIcon> = (props) => {
  const classList = ['infoIcon', classes.infoIcon, classes[props.theme ?? ''], props.className];
  const pathname = usePathname();

  const trackEvent = () => {
    if (props.trackingLabel) {
      // Get the last segment of the URL
      const lastSegment = getLastPathnameSegment(pathname);

      // Use the last segment as location
      // If last segment is empty, fall back to EventCategory.Generic
      const location = lastSegment || EventCategory.Generic;
      tracking.infoButtonClick(location, props.trackingLabel.replace(htmlReplaceRegex, ''));
    }
  };

  const onClick = (e: React.MouseEvent<HTMLAnchorElement> | React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    trackEvent();

    if (props.onClick) {
      props.onClick();
    }
  };
  return (
    <>
      {props.href ? (
        <a
          className={classNames(...classList)}
          href={linkToString(props.href)}
          onClick={props.onClick ? onClick : undefined}
          data-testid="info-icon-button"
        >
          <InfoIconSvg size={props.size} />
        </a>
      ) : (
        <button
          aria-label="Modal öffnen, für mehr Informationen"
          className={classNames('invisible-button', ...classList)}
          onClick={onClick}
          data-testid="info-icon-button"
        >
          <InfoIconSvg size={props.size} />
        </button>
      )}
    </>
  );
};

const InfoIconSvg: React.FC<Omit<InfoIcon, 'trackingLabel'>> = (props) => {
  const size = props.size || 16;
  return (
    <svg
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 12 12"
      width={size}
      height={size}
      fill="currentColor"
    >
      <path
        className={classes.circle}
        fillRule="evenodd"
        clipRule="evenodd"
        d="M6,0.2
      A5.8,5.8,0,1,1,6,11.8
      A5.8,5.8,0,1,1,6,0.2
      M6,1.3
      A4.7,4.7,0,1,1,6,10.7
      A4.7,4.7,0,1,1,6,1.3
      Z"
      />
      <path className={classes.iPoint} d="M6.55 5.44H5.45V8.725H6.55V5.44Z" />
      <path className={classes.iLine} d="M6.55 3.38H5.45V4.805H6.55V3.38Z" />
    </svg>
  );
};

export default InfoIcon;
